import { useState } from "react";
import { useOpcionais, type Opcional, type OpcionalFormData } from "@/features/produtos/hooks/useOpcionais";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function OpcionaisPage() {
  const { opcionais, isLoading, createOpcional, updateOpcional, deleteOpcional } = useOpcionais();

  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editing, setEditing]         = useState<Opcional | null>(null);
  const [deleteId, setDeleteId]       = useState<number | null>(null);
  const [formData, setFormData]       = useState<OpcionalFormData>({
    descricao: "",
    quantidadeMaxima: 1,
  });
  const [busca, setBusca]             = useState("");

  const opcionaisFiltrados = busca
    ? opcionais.filter(o => o.descricao?.toLowerCase().includes(busca.toLowerCase()))
    : opcionais;

  const paginacao = usePaginacaoLocal(opcionaisFiltrados);

  const handleOpenDialog = (opcional?: Opcional) => {
    if (opcional) {
      setEditing(opcional);
      setFormData({
        descricao:       opcional.descricao ?? "",
        quantidadeMaxima: opcional.quantidadeMaxima,
      });
    } else {
      setEditing(null);
      setFormData({ descricao: "", quantidadeMaxima: 1 });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      await updateOpcional.mutateAsync({ id: editing.id, ...formData });
    } else {
      await createOpcional.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Opcionais</h1>
          <p className="text-muted-foreground">Gerencie os opcionais para produtos (restaurante / delivery)</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Opcional
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Qtd. Máxima</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((opcional) => (
              <TableRow key={opcional.id}>
                <TableCell className="font-medium">{opcional.descricao || "-"}</TableCell>
                <TableCell className="text-center">{opcional.quantidadeMaxima}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(opcional)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteId(opcional.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum opcional encontrado para esta busca." : "Nenhum opcional cadastrado"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Opcional" : "Novo Opcional"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qtd">Quantidade Máxima *</Label>
              <Input
                id="qtd"
                type="number"
                min="1"
                value={formData.quantidadeMaxima ?? 1}
                onChange={(e) =>
                  setFormData({ ...formData, quantidadeMaxima: Number(e.target.value) })
                }
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createOpcional.isPending || updateOpcional.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Opcional</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este opcional? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteId) {
                  deleteOpcional.mutate(deleteId);
                  setDeleteId(null);
                }
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
